import { useEffect, useMemo, useState } from 'react';
import type { ShotPositionRow } from '../lib/api';

/**
 * Fullscreen 3-tab overlay the referee can pop after the last shot
 * of a round to compare the before/after snapshots and the analyzed
 * image (after-frame with red markers baked in).
 *
 * The Analyse tab prefers the pre-rendered analyzed blob from
 * `shot_positions.analyzed_blob_id` — a single `<img>` load, no
 * client-side canvas math. For legacy rounds (no shot_positions
 * data) the tab falls back to drawing red circles over the raw
 * after-image using `targetXNorm` / `targetYNorm` from the
 * fallback ladder.
 *
 * Dismiss: top-right ✕, Escape, or backdrop tap. `z-50` keeps it
 * above the existing PiP overlays (`z-40`) on Score.tsx.
 */
export function SnapshotComparison({
  beforeSnapshotId,
  afterSnapshotId,
  positions,
  drift,
  onClose,
}: {
  beforeSnapshotId: string | null;
  afterSnapshotId: string | null;
  positions: ShotPositionRow[];
  drift: boolean;
  onClose: () => void;
}) {
  const [tab, setTab] = useState<'before' | 'after' | 'analysis'>('analysis');

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onClose]);

  // Pick the analyzed blob from the LATEST shot (cumulative — every
  // hit so far is baked in). For legacy rounds we fall back to the
  // raw after image plus client-side markers.
  const latestAnalyzedShotId = useMemo(() => {
    const withBlob = [...positions]
      .filter((p) => p.analyzedBlobId != null)
      .sort((a, b) => b.ord - a.ord);
    return withBlob[0]?.shotId ?? null;
  }, [positions]);

  const beforeUrl = beforeSnapshotId ? `/api/round-snapshots/${beforeSnapshotId}` : null;
  const afterUrl = afterSnapshotId ? `/api/round-snapshots/${afterSnapshotId}` : null;
  const analysisUrl = latestAnalyzedShotId
    ? `/api/shot-position-blobs/${latestAnalyzedShotId}/analyzed`
    : null;

  const caption = (() => {
    if (positions.length === 0) {
      return drift ? 'Analyse · Kamera-Drift erkannt' : 'Analyse · keine neuen Treffer';
    }
    return `Analyse · ${positions.length} ${positions.length === 1 ? 'Treffer' : 'Treffer'} markiert${
      drift ? ' (Drift)' : ''
    }`;
  })();

  return (
    <div className="fixed inset-0 z-50 flex flex-col bg-black/95 text-zinc-100" onClick={onClose}>
      <header
        className="flex flex-none items-center justify-between border-b border-zinc-800 px-4 py-3"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex gap-1">
          <TabButton active={tab === 'before'} onClick={() => setTab('before')}>
            Vorher
          </TabButton>
          <TabButton active={tab === 'after'} onClick={() => setTab('after')}>
            Nachher
          </TabButton>
          <TabButton active={tab === 'analysis'} onClick={() => setTab('analysis')}>
            Analyse
          </TabButton>
        </div>
        <button
          className="min-h-12 min-w-12 rounded-full bg-zinc-800 px-3 py-1.5 text-sm hover:bg-zinc-700"
          onClick={onClose}
          aria-label="Schließen"
        >
          ✕
        </button>
      </header>
      {positions.some((p) => p.estimated) && (
        <p className="px-4 pt-2 text-sm text-amber-300">
          Schusszuordnung geschätzt — Reihenfolge aus dem Endbild nicht erkennbar.
        </p>
      )}
      <div
        className="flex min-h-0 flex-1 flex-col items-center justify-center p-4"
        onClick={(e) => e.stopPropagation()}
      >
        {tab === 'before' && (
          <SnapshotPane url={beforeUrl} emptyLabel="Vorher · nicht aufgenommen" />
        )}
        {tab === 'after' && (
          <SnapshotPane url={afterUrl} emptyLabel="Nachher · nicht aufgenommen" />
        )}
        {tab === 'analysis' && (
          <AnalysisPane
            analysisUrl={analysisUrl}
            fallbackAfterUrl={afterUrl}
            positions={positions}
            caption={caption}
          />
        )}
      </div>
    </div>
  );
}

function TabButton({
  active,
  onClick,
  children,
}: {
  active: boolean;
  onClick: () => void;
  children: React.ReactNode;
}) {
  return (
    <button
      onClick={onClick}
      className={`min-h-12 rounded-full px-4 py-1.5 text-sm font-semibold transition ${
        active ? 'bg-violet-600 text-white' : 'bg-zinc-800 text-zinc-300 hover:bg-zinc-700'
      }`}
    >
      {children}
    </button>
  );
}

function SnapshotPane({ url, emptyLabel }: { url: string | null; emptyLabel: string }) {
  if (!url) {
    return <p className="text-center text-base text-zinc-500">{emptyLabel}</p>;
  }
  // eslint-disable-next-line @next/next/no-img-element — pwa direct fetch
  return <img src={url} alt="" className="max-h-full max-w-full object-contain" />;
}

/**
 * Analyse tab. Two paths:
 *
 * - Modern (post-migration 0057): the pre-rendered analyzed blob is
 *   a single full-bleed `<img>` — markers are baked into the JPEG
 *   server-side via the shared sharp pipeline so there's nothing to
 *   compose here.
 *
 * - Legacy fallback: no analyzed blob yet (older session, or the
 *   per-shot pipeline missed and end-of-round fill hasn't run). Draw
 *   red circles on top of the raw after image using
 *   `targetXNorm/targetYNorm` from the resolved positions. Same
 *   visual contract as the PDF + admin debug page.
 */
function AnalysisPane({
  analysisUrl,
  fallbackAfterUrl,
  positions,
  caption,
}: {
  analysisUrl: string | null;
  fallbackAfterUrl: string | null;
  positions: ShotPositionRow[];
  caption: string;
}) {
  if (analysisUrl) {
    return (
      <figure className="flex min-h-0 flex-1 flex-col items-center justify-center gap-2">
        {/* eslint-disable-next-line @next/next/no-img-element */}
        <img
          src={analysisUrl}
          alt="Analyse"
          className="max-h-[calc(100vh-9rem)] max-w-full object-contain"
        />
        <figcaption className="text-sm text-zinc-400">{caption}</figcaption>
      </figure>
    );
  }
  if (!fallbackAfterUrl) {
    return (
      <p className="text-center text-base text-zinc-500">Analyse · keine Bilddaten verfügbar</p>
    );
  }
  return (
    <figure className="flex min-h-0 flex-1 flex-col items-center justify-center gap-2">
      <LegacyMarkerOverlay afterUrl={fallbackAfterUrl} positions={positions} />
      <figcaption className="text-sm text-zinc-400">{caption}</figcaption>
    </figure>
  );
}

/**
 * Legacy fallback: load the raw after image, measure its on-screen
 * rect, draw red circles at each position's `xNorm`/`yNorm` * rect.
 * Refreshes on window resize so rotation works on phones.
 */
function LegacyMarkerOverlay({
  afterUrl,
  positions,
}: {
  afterUrl: string;
  positions: ShotPositionRow[];
}) {
  const [box, setBox] = useState<{ w: number; h: number } | null>(null);

  useEffect(() => {
    function onResize() {
      // Just bump state — the actual size measure happens in the <img>
      // onLoad handler below. Resize re-renders the wrapper and the
      // image's intrinsic-vs-rendered ratio is recomputed.
      setBox((b) => (b ? { ...b } : null));
    }
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, []);

  return (
    <div className="relative max-h-[calc(100vh-9rem)] max-w-full">
      {/* eslint-disable-next-line @next/next/no-img-element */}
      <img
        src={afterUrl}
        alt="Nachher"
        className="max-h-[calc(100vh-9rem)] max-w-full object-contain"
        onLoad={(e) => {
          const img = e.currentTarget;
          setBox({ w: img.clientWidth, h: img.clientHeight });
        }}
      />
      {box && (
        <svg
          className="pointer-events-none absolute inset-0"
          viewBox={`0 0 ${box.w} ${box.h}`}
          width={box.w}
          height={box.h}
        >
          {positions.map((p) => (
            <circle
              key={p.shotId}
              cx={p.xNorm * box.w}
              cy={p.yNorm * box.h}
              r={Math.max(12, box.w / 60)}
              fill="rgba(220,38,38,0.20)"
              stroke="rgba(220,38,38,0.95)"
              strokeWidth={Math.max(2, box.w / 200)}
            />
          ))}
        </svg>
      )}
    </div>
  );
}
